import { useEffect } from "react";

/**
 * Warns before unsaved changes are lost — on tab close/refresh via
 * `beforeunload`, and on in-app navigation by intercepting clicks on internal
 * links in the capture phase (the router uses declarative <Routes>, so
 * useBlocker's data-router API is not available).
 *
 * The editor's own controls (strategy switcher, phase selection) are not
 * links and stay guarded by the caller.
 */
export function useUnsavedGuard(dirty: boolean, message: string) {
  useEffect(() => {
    if (!dirty) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required by Chrome for the prompt to show.
      e.returnValue = "";
    };

    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") ?? "";
      if (!href.startsWith("/")) return; // external links open elsewhere anyway
      if (href === window.location.pathname) return;
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [dirty, message]);
}
